import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { getStockBuckets, getStockBucketItems, updateStockBucket, deleteStockBucket, deleteStockBucketItem, createStockBucketItem, updateStockBucketItem, } from "../services/api";
import CatalogSearch from "./CatalogSearch";
import "./StockBucketList.css";
export default function StockBucketList({ refreshKey, warehouses }) {
    const { instance } = useMsal();
    const instanceRef = useRef(instance);
    instanceRef.current = instance;
    const [buckets, setBuckets] = useState([]);
    const [bucketItems, setBucketItems] = useState([]);
    const [expandedBucket, setExpandedBucket] = useState(null);
    const [editingBucket, setEditingBucket] = useState(null);
    const [editName, setEditName] = useState("");
    const [editMinStock, setEditMinStock] = useState("");
    const [loading, setLoading] = useState(true);
    const [deletingBucketIds, setDeletingBucketIds] = useState(new Set());
    const [deletingItemIds, setDeletingItemIds] = useState(new Set());
    const [pendingMinStock, setPendingMinStock] = useState(new Map());
    const loadData = useCallback(async (showLoading = false) => {
        if (showLoading)
            setLoading(true);
        try {
            const [b, bi] = await Promise.all([
                getStockBuckets(instanceRef.current),
                getStockBucketItems(instanceRef.current),
            ]);
            setBuckets(b);
            setBucketItems(bi);
        }
        catch (err) {
            console.error("Failed to load stock buckets:", err);
        }
        finally {
            if (showLoading)
                setLoading(false);
        }
    }, []);
    useEffect(() => {
        loadData(true);
    }, [loadData, refreshKey]);
    const itemsByBucket = useMemo(() => {
        const map = new Map();
        for (const item of bucketItems) {
            if (item.stockBucketId) {
                const list = map.get(item.stockBucketId) || [];
                list.push(item);
                map.set(item.stockBucketId, list);
            }
        }
        return map;
    }, [bucketItems]);
    const standaloneItems = useMemo(() => {
        return bucketItems.filter((i) => !i.stockBucketId);
    }, [bucketItems]);
    const warehouseGroups = useMemo(() => {
        const assigned = new Map();
        const unassignedBuckets = [];
        const unassignedItems = [];
        const resolveWarehouseName = (whId, entityName) => {
            return entityName || warehouses.find((w) => w.id === whId)?.name || `Warehouse ${whId}`;
        };
        for (const bucket of buckets) {
            if (bucket.warehouseId) {
                if (!assigned.has(bucket.warehouseId)) {
                    assigned.set(bucket.warehouseId, {
                        name: resolveWarehouseName(bucket.warehouseId, bucket.warehouseName),
                        buckets: [],
                        items: [],
                    });
                }
                assigned.get(bucket.warehouseId).buckets.push(bucket);
            }
            else {
                unassignedBuckets.push(bucket);
            }
        }
        for (const item of standaloneItems) {
            if (item.warehouseId) {
                if (!assigned.has(item.warehouseId)) {
                    assigned.set(item.warehouseId, {
                        name: resolveWarehouseName(item.warehouseId, item.warehouseName),
                        buckets: [],
                        items: [],
                    });
                }
                assigned.get(item.warehouseId).items.push(item);
            }
            else {
                unassignedItems.push(item);
            }
        }
        return { assigned, unassignedBuckets, unassignedItems };
    }, [buckets, standaloneItems, warehouses]);
    const handleToggle = (id) => {
        setExpandedBucket((prev) => (prev === id ? null : id));
        setEditingBucket(null);
    };
    const handleStartEdit = (bucket) => {
        setEditingBucket(bucket.id);
        setEditName(bucket.name);
        setEditMinStock(bucket.minimumStockLevel);
    };
    const handleCancelEdit = () => {
        setEditingBucket(null);
    };
    const handleUpdate = async (id) => {
        try {
            await updateStockBucket(instance, id, {
                name: editName.trim(),
                minimumStockLevel: Number(editMinStock),
            });
            setEditingBucket(null);
            await loadData();
        }
        catch (err) {
            console.error("Failed to update bucket:", err);
        }
    };
    const handleDeleteBucket = async (id) => {
        if (!confirm("Delete this notification group and all its items?"))
            return;
        setDeletingBucketIds((prev) => new Set(prev).add(id));
        try {
            await deleteStockBucket(instanceRef.current, id);
        }
        catch (err) {
            console.error("Failed to delete bucket:", err);
        }
        finally {
            setBuckets((prev) => prev.filter((b) => String(b.id) !== String(id)));
            setBucketItems((prev) => prev.filter((i) => String(i.stockBucketId) !== String(id)));
            setDeletingBucketIds((prev) => {
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
        }
    };
    const handleDeleteItem = async (id) => {
        setDeletingItemIds((prev) => new Set(prev).add(id));
        try {
            await deleteStockBucketItem(instanceRef.current, id);
        }
        catch (err) {
            console.error("Failed to delete item:", err);
        }
        finally {
            setBucketItems((prev) => prev.filter((i) => String(i.id) !== String(id)));
            setDeletingItemIds((prev) => {
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
        }
    };
    const handleAddItemToBucket = async (bucketId, item) => {
        const bucket = buckets.find((b) => b.id === bucketId);
        const warehouseId = bucket?.warehouseId || warehouses[0]?.id;
        const wh = warehouses.find((w) => w.id === warehouseId);
        if (!warehouseId) {
            console.error("No warehouse available");
            return;
        }
        try {
            await createStockBucketItem(instance, {
                stockBucketId: bucketId,
                cwItemId: item.id,
                manufacturerPartNumber: item.manufacturerPartNumber || item.identifier,
                partDescription: item.description,
                warehouseId,
                warehouseName: wh?.name || "",
            });
            await loadData();
        }
        catch (err) {
            console.error("Failed to add item:", err);
        }
    };
    const handleAssignWarehouse = async (bucketId, warehouseId) => {
        try {
            await updateStockBucket(instance, bucketId, { warehouseId });
            await loadData();
        }
        catch (err) {
            console.error("Failed to assign warehouse:", err);
        }
    };
    const renderBucketCard = (bucket, showWarehouseAssign = false) => {
        const items = itemsByBucket.get(bucket.id) || [];
        const isExpanded = expandedBucket === bucket.id;
        const isEditing = editingBucket === bucket.id;
        const existingItemIds = new Set(items.map((i) => i.cwItemId));
        return (_jsxs("div", { className: "bucket-card", children: [_jsxs("div", { className: "bucket-header", onClick: () => handleToggle(bucket.id), children: [_jsxs("span", { className: "bucket-title", children: [isExpanded ? "▼" : "▶", " ", bucket.name, " \u2014 Min Stock:", " ", bucket.minimumStockLevel] }), _jsxs("div", { className: "bucket-actions", onClick: (e) => e.stopPropagation(), children: [showWarehouseAssign && (_jsxs("select", { className: "warehouse-select", defaultValue: "", onClick: (e) => e.stopPropagation(), onChange: (e) => {
                                        if (e.target.value) {
                                            handleAssignWarehouse(bucket.id, Number(e.target.value));
                                        }
                                    }, children: [_jsx("option", { value: "", disabled: true, children: "Set Warehouse..." }), warehouses.map((w) => (_jsx("option", { value: w.id, children: w.name }, w.id)))] })), _jsx("button", { className: "btn-sm btn-edit", onClick: () => handleStartEdit(bucket), children: "Edit" }), _jsx("button", { className: "btn-sm btn-delete", disabled: deletingBucketIds.has(bucket.id), onClick: () => handleDeleteBucket(bucket.id), children: deletingBucketIds.has(bucket.id) ? "Deleting..." : "Delete" })] })] }), isExpanded && (_jsxs("div", { className: "bucket-body", children: [isEditing && (_jsxs("div", { className: "edit-row", children: [_jsxs("div", { className: "edit-group", children: [_jsx("label", { children: "Name" }), _jsx("input", { type: "text", value: editName, onChange: (e) => setEditName(e.target.value) })] }), _jsxs("div", { className: "edit-group", children: [_jsx("label", { children: "Min Stock Level" }), _jsx("input", { type: "number", min: "0", value: editMinStock, onChange: (e) => setEditMinStock(e.target.value === ""
                                                ? ""
                                                : Number(e.target.value)) })] }), _jsx("button", { className: "btn-sm btn-update", onClick: () => handleUpdate(bucket.id), children: "Update" }), _jsx("button", { className: "btn-sm btn-cancel", onClick: handleCancelEdit, children: "Cancel" })] })), items.length === 0 ? (_jsx("p", { style: { color: "#6b7280", fontSize: "0.85rem" }, children: "No items in this group." })) : (_jsxs("table", { className: "items-table", children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { children: "Part Description" }), _jsx("th", { children: "Mfr Part #" }), _jsx("th", {})] }) }), _jsx("tbody", { children: items.map((item) => (_jsxs("tr", { children: [_jsx("td", { children: item.partDescription }), _jsx("td", { children: item.manufacturerPartNumber }), _jsx("td", { children: _jsx("button", { className: "btn-sm btn-delete", disabled: deletingItemIds.has(item.id), onClick: () => handleDeleteItem(item.id), children: deletingItemIds.has(item.id) ? "Deleting..." : "Delete" }) })] }, item.id))) })] })), _jsxs("div", { className: "add-items-section", children: [_jsx("h4", { children: "Add Items to this Group" }), _jsx(CatalogSearch, { onAddItem: (item) => handleAddItemToBucket(bucket.id, item), addedItemIds: existingItemIds })] })] }))] }, bucket.id));
    };
    const renderStandaloneItems = (items, showWarehouseAssign = false) => {
        if (items.length === 0)
            return null;
        return (_jsxs("div", { className: "standalone-items-section", children: [_jsx("h4", { className: "standalone-header", children: "Standalone Items" }), _jsxs("table", { className: "items-table", children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { children: "Part Description" }), _jsx("th", { children: "Mfr Part #" }), _jsx("th", { children: "Min Stock" }), showWarehouseAssign && _jsx("th", { children: "Set Warehouse" }), _jsx("th", {})] }) }), _jsx("tbody", { children: items.map((item) => (_jsxs("tr", { children: [_jsx("td", { children: item.partDescription }), _jsx("td", { children: item.manufacturerPartNumber }), _jsx("td", { children: _jsxs("div", { className: "min-stock-inline", children: [_jsx("input", { type: "number", min: "0", className: "inline-min-stock", value: pendingMinStock.has(item.id) ? pendingMinStock.get(item.id) : (item.minimumStockLevel ?? 0), onChange: (e) => {
                                                        const val = e.target.value === "" ? 0 : Number(e.target.value);
                                                        setPendingMinStock((prev) => {
                                                            const next = new Map(prev);
                                                            if (val === (item.minimumStockLevel ?? 0)) {
                                                                next.delete(item.id);
                                                            }
                                                            else {
                                                                next.set(item.id, val);
                                                            }
                                                            return next;
                                                        });
                                                    } }), pendingMinStock.has(item.id) && (_jsx("button", { className: "btn-sm btn-update", onClick: () => {
                                                        const val = pendingMinStock.get(item.id);
                                                        updateStockBucketItem(instance, item.id, {
                                                            minimumStockLevel: val,
                                                        }).then(() => {
                                                            setPendingMinStock((prev) => {
                                                                const next = new Map(prev);
                                                                next.delete(item.id);
                                                                return next;
                                                            });
                                                            loadData();
                                                        });
                                                    }, children: "Update" }))] }) }), showWarehouseAssign && (_jsx("td", { children: _jsxs("select", { className: "warehouse-select", defaultValue: "", onChange: (e) => {
                                                if (e.target.value) {
                                                    const wh = warehouses.find((w) => w.id === Number(e.target.value));
                                                    if (wh) {
                                                        updateStockBucketItem(instance, item.id, {
                                                            warehouseId: wh.id,
                                                            warehouseName: wh.name,
                                                        }).then(() => loadData());
                                                    }
                                                }
                                            }, children: [_jsx("option", { value: "", disabled: true, children: "Set Warehouse..." }), warehouses.map((w) => (_jsx("option", { value: w.id, children: w.name }, w.id)))] }) })), _jsx("td", { children: _jsx("button", { className: "btn-sm btn-delete", disabled: deletingItemIds.has(item.id), onClick: () => handleDeleteItem(item.id), children: deletingItemIds.has(item.id) ? "Deleting..." : "Delete" }) })] }, item.id))) })] })] }));
    };
    if (loading) {
        return (_jsxs("div", { className: "stock-bucket-list", children: [_jsx("h2", { children: "Current Notification Groups" }), _jsx("p", { className: "loading", children: "Loading..." })] }));
    }
    return (_jsxs("div", { className: "stock-bucket-list", children: [_jsx("h2", { children: "Current Notification Groups" }), buckets.length === 0 && standaloneItems.length === 0 && (_jsx("p", { className: "no-buckets", children: "No notification groups or items found." })), Array.from(warehouseGroups.assigned.entries()).map(([whId, group]) => (_jsxs("div", { className: "warehouse-group", children: [_jsx("h3", { className: "warehouse-group-header", children: group.name }), group.buckets.map((bucket) => renderBucketCard(bucket)), renderStandaloneItems(group.items)] }, whId))), (warehouseGroups.unassignedBuckets.length > 0 || warehouseGroups.unassignedItems.length > 0) && (_jsxs("div", { className: "warehouse-group unassigned-group", children: [_jsx("h3", { className: "warehouse-group-header unassigned-header", children: "Unassigned \u2014 No Warehouse" }), warehouseGroups.unassignedBuckets.map((bucket) => renderBucketCard(bucket, true)), renderStandaloneItems(warehouseGroups.unassignedItems, true)] }))] }));
}
